// src/components/EditCarrier.js
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  useParams,
  useNavigate,
  useLocation,
} from "react-router-dom";

import axios from "../axiosConfig";

import Select from "react-select";

import {
  customSelectStyles,
} from "../styles/reactSelectStyles";

import {
  FaArrowLeft,
  FaCalendarAlt,
  FaCreditCard,
  FaFileAlt,
  FaFrown,
  FaMapMarkerAlt,
  FaMeh,
  FaPlus,
  FaSave,
  FaShieldAlt,
  FaSmile,
  FaTrashAlt,
  FaTruck,
  FaUpload,
} from "react-icons/fa";

import {
  useTranslation,
} from "react-i18next";

import "../styles/EditCarrier.css";

import {
  readCarrierCache,
  writeCarrierCache,
} from "../lib/carrierEditCache";

/* =========================================================
   Static options
   ========================================================= */

const countryOptions = [
  {
    labelKey: "countries.austria",
    value: "Austria",
    code: "at",
  },
  {
    labelKey: "countries.belgium",
    value: "Belgium",
    code: "be",
  },
  {
    labelKey: "countries.croatia",
    value: "Croatia",
    code: "hr",
  },
  {
    labelKey: "countries.czech_republic",
    value: "Czech Republic",
    code: "cz",
  },
  {
    labelKey: "countries.denmark",
    value: "Denmark",
    code: "dk",
  },
  {
    labelKey: "countries.estonia",
    value: "Estonia",
    code: "ee",
  },
  {
    labelKey: "countries.finland",
    value: "Finland",
    code: "fi",
  },
  {
    labelKey: "countries.france",
    value: "France",
    code: "fr",
  },
  {
    labelKey: "countries.germany",
    value: "Germany",
    code: "de",
  },
  {
    labelKey: "countries.hungary",
    value: "Hungary",
    code: "hu",
  },
  {
    labelKey: "countries.italy",
    value: "Italy",
    code: "it",
  },
  {
    labelKey: "countries.netherlands",
    value: "Netherlands",
    code: "nl",
  },
  {
    labelKey: "countries.norway",
    value: "Norway",
    code: "no",
  },
  {
    labelKey: "countries.poland",
    value: "Poland",
    code: "pl",
  },
  {
    labelKey: "countries.portugal",
    value: "Portugal",
    code: "pt",
  },
  {
    labelKey: "countries.spain",
    value: "Spain",
    code: "es",
  },
  {
    labelKey: "countries.sweden",
    value: "Sweden",
    code: "se",
  },
  {
    labelKey: "countries.switzerland",
    value: "Switzerland",
    code: "ch",
  },
  {
    labelKey: "countries.united_kingdom",
    value: "United Kingdom",
    code: "gb",
  },
];

const phoneNumberOptions = [
  {
    value: "+43",
    labelKey: "phone.austria",
    code: "at",
  },
  {
    value: "+32",
    labelKey: "phone.belgium",
    code: "be",
  },
  {
    value: "+385",
    labelKey: "phone.croatia",
    code: "hr",
  },
  {
    value: "+420",
    labelKey: "phone.czech_republic",
    code: "cz",
  },
  {
    value: "+45",
    labelKey: "phone.denmark",
    code: "dk",
  },
  {
    value: "+351",
    labelKey: "phone.portugal",
    code: "pt",
  },
  {
    value: "+34",
    labelKey: "phone.spain",
    code: "es",
  },
];

const currencyOptions = [
  {
    value: "EUR",
    labelKey: "currencies.eur",
    code: "eu",
  },
  {
    value: "USD",
    labelKey: "currencies.usd",
    code: "us",
  },
  {
    value: "GBP",
    labelKey: "currencies.gbp",
    code: "gb",
  },
  {
    value: "CHF",
    labelKey: "currencies.chf",
    code: "ch",
  },
  {
    value: "PLN",
    labelKey: "currencies.pln",
    code: "pl",
  },
  {
    value: "NOK",
    labelKey: "currencies.nok",
    code: "no",
  },
  {
    value: "SEK",
    labelKey: "currencies.sek",
    code: "se",
  },
  {
    value: "DKK",
    labelKey: "currencies.dkk",
    code: "dk",
  },
  {
    value: "ISK",
    labelKey: "currencies.isk",
    code: "is",
  },
  {
    value: "CZK",
    labelKey: "currencies.czk",
    code: "cz",
  },
  {
    value: "HUF",
    labelKey: "currencies.huf",
    code: "hu",
  },
  {
    value: "RON",
    labelKey: "currencies.ron",
    code: "ro",
  },
  {
    value: "BGN",
    labelKey: "currencies.bgn",
    code: "bg",
  },
  {
    value: "RSD",
    labelKey: "currencies.rsd",
    code: "rs",
  },
  {
    value: "MKD",
    labelKey: "currencies.mkd",
    code: "mk",
  },
  {
    value: "BAM",
    labelKey: "currencies.bam",
    code: "ba",
  },
  {
    value: "ALL",
    labelKey: "currencies.all",
    code: "al",
  },
  {
    value: "MDL",
    labelKey: "currencies.mdl",
    code: "md",
  },
  {
    value: "UAH",
    labelKey: "currencies.uah",
    code: "ua",
  },
  {
    value: "TRY",
    labelKey: "currencies.try",
    code: "tr",
  },
];

const TRUCK_TYPES = [
  "Curtain Side Truck (Tautliner)",
  "Refrigerated Truck (Frigo)",
  "Articulated Dump Truck",
  "Box Truck / Van",
  "Car Hauler",
  "Cattle Carrier",
  "Cement Mixer Truck",
  "Clothing Hanging Carrier",
  "Concrete Pump Truck",
  "Container Truck",
  "Crane Truck",
  "Double Decker Truck",
  "Drum Carrier",
  "Dump Truck (Dump Carrier)",
  "Flatbed Truck",
  "Heavy Hauler",
  "Logging Truck",
  "Lowboy Truck (Special Transport)",
  "Open Box Truck",
  "Pickup Truck",
  "Road Train",
  "Tanker Truck (Cistern)",
  "Vehicle Carrier",
];

const SERVICES_OFFERED = [
  "Container Transport",
  "Dangerous Goods Transport (ADR)",
  "Express or Time-Critical Delivery",
  "Fragile or High-Value Goods Transport",
  "Full Truck Load",
  "Groupage",
  "Intermodal Transportation",
  "Last-Mile Delivery",
  "Livestock Transport",
  "Oversized or Heavy Haul Transport",
  "Reverse Logistics",
  "Specialized Cargo Transport",
  "Temperature-Controlled Transport",
  "Vehicle Transport",
  "Waste Transport",
];

const EUROPE_COUNTRIES = [
  "Austria",
  "Belgium",
  "Bulgaria",
  "Croatia",
  "Cyprus",
  "Czech Republic",
  "Denmark",
  "Estonia",
  "Finland",
  "France",
  "Germany",
  "Greece",
  "Hungary",
  "Ireland",
  "Italy",
  "Latvia",
  "Lithuania",
  "Luxembourg",
  "Malta",
  "Netherlands",
  "Poland",
  "Portugal",
  "Romania",
  "Slovakia",
  "Slovenia",
  "Spain",
  "Sweden",
];

const USA_STATES = [
  "Alabama",
  "Alaska",
  "Arizona",
  "Arkansas",
  "California",
  "Colorado",
  "Connecticut",
  "Delaware",
  "Florida",
  "Georgia",
  "Hawaii",
  "Idaho",
  "Illinois",
  "Indiana",
  "Iowa",
  "Kansas",
  "Kentucky",
  "Louisiana",
  "Maine",
  "Maryland",
  "Massachusetts",
  "Michigan",
  "Minnesota",
  "Mississippi",
  "Missouri",
  "Montana",
  "Nebraska",
  "Nevada",
  "New Hampshire",
  "New Jersey",
  "New Mexico",
  "New York",
  "North Carolina",
  "North Dakota",
  "Ohio",
  "Oklahoma",
  "Oregon",
  "Pennsylvania",
  "Rhode Island",
  "South Carolina",
  "South Dakota",
  "Tennessee",
  "Texas",
  "Utah",
  "Vermont",
  "Virginia",
  "Washington",
  "West Virginia",
  "Wisconsin",
  "Wyoming",
];

const PAYMENT_DUE_DATES = [
  "same day",
  "1 day",
  "3 days",
  "5 days",
  "10 days",
  "15 days",
  "30 days",
  "45 days",
  "60 days",
  "90 days",
  "120 days",
];

/* =========================================================
   Default carrier
   ========================================================= */

const DEFAULT_CARRIER = {
  companyName: "",
  fleetSize: "",
  truckTypes: [],
  serviceAreas: [],
  loadCapacityPerTruck: "",
  driversEmployed: "",
  servicesOffered: [],
  partnershipHistory: "",

  address: "",
  city: "",
  postalCode: "",
  country: "",

  mainContactName: "",
  mainContactTitle: "",
  contactEmail: "",
  phoneNumberCountryCode: "",
  phoneNumber: "",

  contacts: [],

  website: "",

  communityLicense: null,
  communityLicenseExpiry: "",

  cmrInsurance: null,
  cmrInsuranceExpiry: "",

  motorThirdPartyLiabilityInsurance: null,
  motorThirdPartyLiabilityInsuranceExpiry: "",

  comprehensiveVehicleInsurance: null,
  ComprehensiveVehicleInsuranceExpiryDate: "",

  bankName: "",
  accountHolderName: "",
  accountNumber: "",
  iban: "",
  swift_bic: "",
  currency: "",

  paymentMethodsAccepted: [],
  paymentDueDate: "",
  discountTerms: "",
  latePaymentPenalties: "",

  VAT_TaxInformation: "",
  iva: "",
  invoiceDeliveryMethod: [],
  invoiceEmail: "",

  additionalNotes: "",
};

/* =========================================================
   Helpers
   ========================================================= */

const formatDateForInput = (value) => {
  if (!value) return "";

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return "";
  }

  return date
    .toISOString()
    .split("T")[0];
};

const countOnlyAlphaNumeric = (
  value = ""
) =>
  String(value).replace(
    /[^a-zA-Z0-9]/g,
    ""
  ).length;

const toArr = (value) => {
  if (
    Array.isArray(value)
  ) {
    return value;
  }

  return String(
    value || ""
  )
    .split(",")
    .map((item) =>
      item.trim()
    )
    .filter(Boolean);
};

const normalizeContacts = (
  contacts
) =>
  Array.isArray(contacts)
    ? contacts.map(
        (contact) => ({
          contactName:
            contact?.contactName ||
            "",

          contactTitle:
            contact?.contactTitle ||
            "",

          contactEmail:
            contact?.contactEmail ||
            "",

          phoneNumberCountryCode:
            contact?.phoneNumberCountryCode ||
            "",

          phoneNumber:
            contact?.phoneNumber ||
            "",
        })
      )
    : [];

const normalizePrefill = (
  prefill
) => {
  if (!prefill) {
    return null;
  }

  const out = {
    ...prefill,
  };

  if (
    prefill.communityLicenseExpiry !=
    null
  ) {
    out.communityLicenseExpiry =
      formatDateForInput(
        prefill.communityLicenseExpiry
      );
  }

  if (
    prefill.cmrInsuranceExpiry !=
    null
  ) {
    out.cmrInsuranceExpiry =
      formatDateForInput(
        prefill.cmrInsuranceExpiry
      );
  }

  if (
    prefill.motorThirdPartyLiabilityInsuranceExpiry !=
    null
  ) {
    out.motorThirdPartyLiabilityInsuranceExpiry =
      formatDateForInput(
        prefill.motorThirdPartyLiabilityInsuranceExpiry
      );
  }

  if (
    prefill.ComprehensiveVehicleInsuranceExpiryDate !=
    null
  ) {
    out.ComprehensiveVehicleInsuranceExpiryDate =
      formatDateForInput(
        prefill.ComprehensiveVehicleInsuranceExpiryDate
      );
  }

  if (
    prefill.truckTypes !=
    null
  ) {
    out.truckTypes =
      toArr(
        prefill.truckTypes
      );
  }

  if (
    prefill.serviceAreas !=
    null
  ) {
    out.serviceAreas =
      toArr(
        prefill.serviceAreas
      );
  }

  if (
    prefill.servicesOffered !=
    null
  ) {
    out.servicesOffered =
      toArr(
        prefill.servicesOffered
      );
  }

  if (
    prefill.paymentMethodsAccepted !=
    null
  ) {
    out.paymentMethodsAccepted =
      toArr(
        prefill.paymentMethodsAccepted
      );
  }

  if (
    prefill.invoiceDeliveryMethod !=
    null
  ) {
    out.invoiceDeliveryMethod =
      toArr(
        prefill.invoiceDeliveryMethod
      );
  }

  out.contacts =
    normalizeContacts(
      prefill.contacts
    );

  return out;
};

const normalizeCarrierObj = (
  value
) => {
  if (!value) {
    return null;
  }

  const out = {
    ...value,
  };

  if (
    out.communityLicenseExpiry !=
    null
  ) {
    out.communityLicenseExpiry =
      formatDateForInput(
        out.communityLicenseExpiry
      );
  }

  if (
    out.cmrInsuranceExpiry !=
    null
  ) {
    out.cmrInsuranceExpiry =
      formatDateForInput(
        out.cmrInsuranceExpiry
      );
  }

  if (
    out.motorThirdPartyLiabilityInsuranceExpiry !=
    null
  ) {
    out.motorThirdPartyLiabilityInsuranceExpiry =
      formatDateForInput(
        out.motorThirdPartyLiabilityInsuranceExpiry
      );
  }

  if (
    out.ComprehensiveVehicleInsuranceExpiryDate !=
    null
  ) {
    out.ComprehensiveVehicleInsuranceExpiryDate =
      formatDateForInput(
        out.ComprehensiveVehicleInsuranceExpiryDate
      );
  }

  out.truckTypes =
    toArr(out.truckTypes);

  out.serviceAreas =
    toArr(
      out.serviceAreas
    );

  out.servicesOffered =
    toArr(
      out.servicesOffered
    );

  out.paymentMethodsAccepted =
    toArr(
      out.paymentMethodsAccepted
    );

  out.invoiceDeliveryMethod =
    toArr(
      out.invoiceDeliveryMethod
    );

  out.contacts =
    normalizeContacts(
      out.contacts
    );

  out.driversEmployed =
    out.driversEmployed ??
    "";

  out.loadCapacityPerTruck =
    out.loadCapacityPerTruck ??
    "";

  out.partnershipHistory =
    out.partnershipHistory ??
    "";

  out.iva =
    out.iva ?? "";

  return out;
};

const getFileName = (
  file,
  fallback
) => {
  if (!file) {
    return fallback;
  }

  if (
    typeof file !==
    "string"
  ) {
    return (
      file.name ||
      fallback
    );
  }

  try {
    const withoutQuery =
      file.split("?")[0];

    const lastSegment =
      withoutQuery
        .split("/")
        .pop();

    return (
      decodeURIComponent(
        lastSegment
      ) ||
      fallback
    );
  } catch {
    return fallback;
  }
};

const openFile = (
  file
) => {
  if (!file) return;

  if (
    typeof file ===
    "string"
  ) {
    window.open(
      file,
      "_blank",
      "noopener,noreferrer"
    );

    return;
  }

  const objectUrl =
    URL.createObjectURL(
      file
    );

  window.open(
    objectUrl,
    "_blank",
    "noopener,noreferrer"
  );

  setTimeout(() => {
    URL.revokeObjectURL(
      objectUrl
    );
  }, 60000);
};

/* =========================================================
   Multi Select
   ========================================================= */

const multiInlineScrollStyles = {
  ...customSelectStyles,

  control: (
    base,
    state
  ) => ({
    ...(customSelectStyles.control
      ? customSelectStyles.control(
          base,
          state
        )
      : base),

    minHeight: 40,
    height: 40,
    overflow: "hidden",
  }),

  valueContainer: (
    base
  ) => ({
    ...(customSelectStyles.valueContainer
      ? customSelectStyles.valueContainer(
          base
        )
      : base),

    flexWrap: "nowrap",

    overflowX: "auto",
    overflowY: "hidden",

    whiteSpace: "nowrap",

    alignItems: "center",

    paddingRight: 68,

    gap: 5,
  }),

  multiValue: (
    base
  ) => ({
    ...(customSelectStyles.multiValue
      ? customSelectStyles.multiValue(
          base
        )
      : base),

    flex: "0 0 auto",
    maxWidth: "none",
    margin: 0,
  }),

  multiValueLabel: (
    base
  ) => ({
    ...(customSelectStyles.multiValueLabel
      ? customSelectStyles.multiValueLabel(
          base
        )
      : base),

    maxWidth: 150,

    overflow: "hidden",

    textOverflow:
      "ellipsis",

    whiteSpace:
      "nowrap",
  }),

  indicatorsContainer: (
    base
  ) => ({
    ...(customSelectStyles.indicatorsContainer
      ? customSelectStyles.indicatorsContainer(
          base
        )
      : base),

    alignItems:
      "center",
  }),
};

/* =========================================================
   Document row
   ========================================================= */

function CarrierDocumentRow({
  file,
  fileKey,
  inputId,
  label,
  expiryName,
  expiryValue,
  expiryRequired = false,
  fileError,
  expiryError,
  handleFileChange,
  handleInputChange,
  t,
}) {
  const fileName =
    getFileName(
      file,
      t(
        "documents.no_file",
        {
          defaultValue:
            "Nenhum ficheiro carregado",
        }
      )
    );

  const openDatePicker =
    () => {
      const input =
        document.getElementById(
          expiryName
        );

      input?.focus();

      if (
        typeof input?.showPicker ===
        "function"
      ) {
        input.showPicker();
      }
    };

  return (
    <div
      className={`edit-carrier-document-row ${
        fileError ||
        expiryError
          ? "has-error"
          : ""
      }`}
    >
      <div className="edit-carrier-document-info">
        <span className="edit-carrier-document-icon">
          <FaFileAlt />
        </span>

        <div className="edit-carrier-document-copy">
          <span className="edit-carrier-document-name">
            {label}
          </span>

          {file ? (
            <button
              type="button"
              className="edit-carrier-document-file"
              title={fileName}
              onClick={() =>
                openFile(file)
              }
            >
              {fileName}
            </button>
          ) : (
            <span className="edit-carrier-document-empty">
              {fileName}
            </span>
          )}

          {fileError && (
            <p className="edit-carrier-field-error">
              {fileError}
            </p>
          )}
        </div>
      </div>

      <div className="edit-carrier-document-action">
        <button
          type="button"
          className="edit-carrier-document-change"
          onClick={() =>
            document
              .getElementById(
                inputId
              )
              ?.click()
          }
        >
          {file ? (
            t("actions.change")
          ) : (
            <>
              <FaUpload />

              {t(
                "documents.upload",
                {
                  defaultValue:
                    "Carregar",
                }
              )}
            </>
          )}
        </button>

        <input
          type="file"
          id={inputId}
          name={fileKey}
          accept=".jpeg,.jpg,.png,.pdf"
          onChange={
            handleFileChange
          }
          className="edit-carrier-hidden-input"
        />
      </div>

      <div className="edit-carrier-document-expiry">
        <div className="edit-carrier-custom-input-wrapper">
          <input
            type="date"
            id={expiryName}
            name={expiryName}
            value={
              expiryValue ||
              ""
            }
            onChange={
              handleInputChange
            }
            className={`edit-carrier-date-input ${
              expiryError
                ? "edit-carrier-input-error"
                : ""
            }`}
            aria-label={`${t(
              "fields.expiryDate"
            )}${
              expiryRequired
                ? " *"
                : ""
            }`}
          />

          <FaCalendarAlt
            className="edit-carrier-input-icon edit-carrier-fixed-calendar"
            onClick={(
              event
            ) => {
              event.preventDefault();

              openDatePicker();
            }}
          />
        </div>

        {expiryError && (
          <p className="edit-carrier-field-error">
            {expiryError}
          </p>
        )}
      </div>
    </div>
  );
}

/* =========================================================
   EditCarrier
   ========================================================= */

export default function EditCarrier() {
  const {
    t,
  } = useTranslation(
    "edit_carrier"
  );

  const {
    carrierId,
  } = useParams();

  const navigate =
    useNavigate();

  const location =
    useLocation();

  /* =======================================================
     Options
     ======================================================= */

  const paymentOptions =
    useMemo(
      () => [
        {
          value:
            "Bank Transfer",

          label:
            t(
              "payments.bank_transfer"
            ),
        },
        {
          value:
            "Credit/Debit Card",

          label:
            t(
              "payments.card"
            ),
        },
        {
          value:
            "Cash",

          label:
            t(
              "payments.cash"
            ),
        },
        {
          value:
            "MB WAY",

          label:
            t(
              "payments.mbway"
            ),
        },
        {
          value:
            "PayPal",

          label:
            t(
              "payments.paypal"
            ),
        },
      ],
      [t]
    );

  const invoiceDeliveryOptions =
    useMemo(
      () => [
        {
          value: "email",

          label:
            t(
              "invoice_delivery.email"
            ),
        },
        {
          value: "postal",

          label:
            t(
              "invoice_delivery.postal"
            ),
        },
        {
          value:
            "download",

          label:
            t(
              "invoice_delivery.download"
            ),
        },
      ],
      [t]
    );

  const mapCountryOpts =
    useMemo(
      () =>
        countryOptions.map(
          (option) => ({
            ...option,

            label:
              t(
                option.labelKey
              ),
          })
        ),
      [t]
    );

  const mapPhoneOpts =
    useMemo(
      () =>
        phoneNumberOptions.map(
          (option) => ({
            ...option,

            label:
              t(
                option.labelKey
              ),
          })
        ),
      [t]
    );

  const mapCurrencyOpts =
    useMemo(
      () =>
        currencyOptions.map(
          (option) => ({
            ...option,

            label:
              t(
                option.labelKey
              ),
          })
        ),
      [t]
    );

  const truckTypeOptions =
    useMemo(
      () =>
        TRUCK_TYPES.map(
          (value) => ({
            value,

            label:
              t(
                `truck_types.${value}`
              ),
          })
        ),
      [t]
    );

  const servicesOfferedOptions =
    useMemo(
      () =>
        SERVICES_OFFERED.map(
          (value) => ({
            value,

            label:
              t(
                `services.${value}`
              ),
          })
        ),
      [t]
    );

  const europeCountries =
    useMemo(
      () =>
        EUROPE_COUNTRIES.map(
          (value) => ({
            value,

            label:
              t(
                `countries_list.${value}`
              ),
          })
        ),
      [t]
    );

  const usaStates =
    useMemo(
      () =>
        USA_STATES.map(
          (value) => ({
            value,

            label:
              t(
                `usa_states.${value}`
              ),
          })
        ),
      [t]
    );

  const paymentDueDateOptions =
    useMemo(
      () =>
        PAYMENT_DUE_DATES.map(
          (value) => ({
            value,

            label:
              t(
                `due_dates.${value}`
              ),
          })
        ),
      [t]
    );

  const partnershipOptions =
    useMemo(
      () => [
        {
          value:
            "Positive",

          label:
            t(
              "partnership.positive"
            ),

          icon: (
            <FaSmile
              color="limegreen"
            />
          ),
        },
        {
          value:
            "Neutral",

          label:
            t(
              "partnership.neutral"
            ),

          icon: (
            <FaMeh
              color="gray"
            />
          ),
        },
        {
          value:
            "Negative",

          label:
            t(
              "partnership.negative"
            ),

          icon: (
            <FaFrown
              color="tomato"
            />
          ),
        },
      ],
      [t]
    );

  /* =======================================================
     Validation
     ======================================================= */

  const validateBasic =
    (formData) => {
      const nextErrors =
        {};

      if (
        !String(
          formData.companyName ||
            ""
        ).trim()
      ) {
        nextErrors.companyName =
          t(
            "errors.companyName_required"
          );
      }

      return nextErrors;
    };

  const validateContacts =
    (formData) => {
      const nextErrors =
        {};

      const normalizedEmailMap =
        {};

      (
        formData.contacts ||
        []
      ).forEach(
        (
          contact,
          index
        ) => {
          const trimmedEmail =
            String(
              contact.contactEmail ||
                ""
            ).trim();

          if (!trimmedEmail) {
            return;
          }

          const normalizedEmail =
            trimmedEmail.toLowerCase();

          if (
            !normalizedEmailMap[
              normalizedEmail
            ]
          ) {
            normalizedEmailMap[
              normalizedEmail
            ] = [];
          }

          normalizedEmailMap[
            normalizedEmail
          ].push(index);
        }
      );

      Object.values(
        normalizedEmailMap
      ).forEach(
        (indexes) => {
          if (
            indexes.length >
            1
          ) {
            indexes.forEach(
              (index) => {
                nextErrors[
                  `contacts-${index}-contactEmail`
                ] =
                  t(
                    "errors.duplicate_contact_email",
                    {
                      defaultValue:
                        "Este e-mail de contacto já está a ser utilizado.",
                    }
                  );
              }
            );
          }
        }
      );

      return nextErrors;
    };

  const validateDocuments =
    (nextFiles) => {
      const nextErrors =
        {};

      const maxFileSize =
        5 *
        1024 *
        1024;

      Object.entries(
        nextFiles
      ).forEach(
        ([
          key,
          file,
        ]) => {
          if (
            file &&
            typeof file !==
              "string" &&
            file.size >
              maxFileSize
          ) {
            nextErrors[key] =
              t(
                "errors.file_too_big"
              );
          }
        }
      );

      return nextErrors;
    };

  const validateBilling =
    (formData) => {
      const nextErrors =
        {};

      if (
        formData.accountNumber &&
        formData
          .accountNumber
          .length < 8
      ) {
        nextErrors.accountNumber =
          t(
            "errors.accountNumber_min"
          );
      }

      if (
        formData.iban &&
        countOnlyAlphaNumeric(
          formData.iban
        ) < 15
      ) {
        nextErrors.iban =
          t(
            "errors.iban_min"
          );
      }

      if (
        formData.swift_bic &&
        countOnlyAlphaNumeric(
          formData.swift_bic
        ) < 8
      ) {
        nextErrors.swift_bic =
          t(
            "errors.swift_min"
          );
      }

      if (
        String(
          formData.iva ||
            ""
        ).trim()
      ) {
        const normalizedIva =
          String(
            formData.iva ||
              ""
          )
            .trim()
            .replace(
              ",",
              "."
            );

        const parsedIva =
          Number(
            normalizedIva
          );

        if (
          !Number.isFinite(
            parsedIva
          )
        ) {
          nextErrors.iva =
            t(
              "errors.iva_invalid",
              {
                defaultValue:
                  "O IVA deve ser um número válido.",
              }
            );
        } else if (
          parsedIva < 0 ||
          parsedIva > 100
        ) {
          nextErrors.iva =
            t(
              "errors.iva_range",
              {
                defaultValue:
                  "O IVA deve estar entre 0 e 100.",
              }
            );
        }
      }

      return nextErrors;
    };

  /* =======================================================
     Initial data/cache
     ======================================================= */

  const prefill =
    location.state
      ?.prefill ||
    location.state
      ?.carrier ||
    null;

  const normalizedPrefill =
    useMemo(
      () =>
        normalizePrefill(
          prefill
        ),
      [prefill]
    );

  const [
    cacheInit,
  ] = useState(
    () =>
      readCarrierCache(
        carrierId
      )
  );

  const [
    bootstrapped,
    setBootstrapped,
  ] = useState(
    () =>
      Boolean(
        normalizedPrefill ||
          cacheInit
      )
  );

  const dirtyRef =
    useRef(false);

  const cacheTimerRef =
    useRef(null);

  const sectionRefs =
    useRef({});

  const [
    activeSection,
    setActiveSection,
  ] = useState(
    "basic"
  );

  const [
    errors,
    setErrors,
  ] = useState({});

  const [
    errorMessage,
    setErrorMessage,
  ] = useState("");

  const [
    showSuccessPopup,
    setShowSuccessPopup,
  ] = useState(false);

  const [
    isSaving,
    setIsSaving,
  ] = useState(false);

  const [
    carrier,
    setCarrier,
  ] = useState(
    () => ({
      ...DEFAULT_CARRIER,

      ...(cacheInit?.carrier ||
        {}),

      ...(normalizedPrefill ||
        {}),
    })
  );

  const [
    files,
    setFiles,
  ] = useState(
    () => ({
      communityLicense:
        normalizedPrefill
          ?.communityLicense ??
        cacheInit?.files
          ?.communityLicense ??
        prefill
          ?.communityLicense ??
        null,

      cmrInsurance:
        normalizedPrefill
          ?.cmrInsurance ??
        cacheInit?.files
          ?.cmrInsurance ??
        prefill
          ?.cmrInsurance ??
        null,

      motorThirdPartyLiabilityInsurance:
        normalizedPrefill
          ?.motorThirdPartyLiabilityInsurance ??
        cacheInit?.files
          ?.motorThirdPartyLiabilityInsurance ??
        prefill
          ?.motorThirdPartyLiabilityInsurance ??
        null,

      comprehensiveVehicleInsurance:
        normalizedPrefill
          ?.comprehensiveVehicleInsurance ??
        cacheInit?.files
          ?.comprehensiveVehicleInsurance ??
        prefill
          ?.comprehensiveVehicleInsurance ??
        null,
    })
  );

  /* =======================================================
     Cache
     ======================================================= */

  useEffect(() => {
    return () => {
      if (
        cacheTimerRef.current
      ) {
        clearTimeout(
          cacheTimerRef.current
        );
      }
    };
  }, []);

  const scheduleCacheWrite =
    (
      nextCarrier,
      nextFiles
    ) => {
      try {
        if (
          cacheTimerRef.current
        ) {
          clearTimeout(
            cacheTimerRef.current
          );
        }

        cacheTimerRef.current =
          setTimeout(
            () => {
              writeCarrierCache(
                carrierId,
                {
                  carrier:
                    nextCarrier,

                  files:
                    nextFiles,
                }
              );
            },
            350
          );
      } catch {
        // ignore
      }
    };

  /* =======================================================
     Fetch full carrier
     ======================================================= */

  useEffect(() => {
    const controller =
      new AbortController();

    (async () => {
      try {
        const {
          data,
        } =
          await axios.get(
            `/carriers/${carrierId}`,
            {
              signal:
                controller.signal,
            }
          );

        const hydratedCarrier =
          normalizeCarrierObj({
            ...DEFAULT_CARRIER,
            ...data,
          });

        const hydratedFiles =
          {
            communityLicense:
              data.communityLicense ||
              null,

            cmrInsurance:
              data.cmrInsurance ||
              null,

            motorThirdPartyLiabilityInsurance:
              data.motorThirdPartyLiabilityInsurance ||
              null,

            comprehensiveVehicleInsurance:
              data.comprehensiveVehicleInsurance ||
              null,
          };

        setBootstrapped(
          true
        );

        writeCarrierCache(
          carrierId,
          {
            carrier:
              hydratedCarrier,

            files:
              hydratedFiles,
          }
        );

        if (
          dirtyRef.current
        ) {
          return;
        }

        setCarrier(
          (previous) => {
            try {
              return JSON.stringify(
                previous
              ) ===
                JSON.stringify(
                  hydratedCarrier
                )
                ? previous
                : hydratedCarrier;
            } catch {
              return hydratedCarrier;
            }
          }
        );

        setFiles(
          (previous) => {
            try {
              return JSON.stringify(
                previous
              ) ===
                JSON.stringify(
                  hydratedFiles
                )
                ? previous
                : hydratedFiles;
            } catch {
              return hydratedFiles;
            }
          }
        );
      } catch (error) {
        if (
          String(
            error?.name
          ) ===
            "CanceledError" ||
          String(
            error?.name
          ) ===
            "AbortError"
        ) {
          return;
        }

        console.error(
          "Error fetching carrier details:",
          error
        );

        setErrorMessage(
          t(
            "feedback.load_failed"
          )
        );
      }
    })();

    return () =>
      controller.abort();
  }, [
    carrierId,
    t,
  ]);

  useEffect(() => {
    if (
      !normalizedPrefill
    ) {
      return;
    }

    setBootstrapped(
      true
    );

    const mergedCarrier =
      {
        ...DEFAULT_CARRIER,

        ...(cacheInit?.carrier ||
          {}),

        ...normalizedPrefill,
      };

    const mergedFiles =
      {
        ...(cacheInit?.files ||
          {}),

        ...files,
      };

    writeCarrierCache(
      carrierId,
      {
        carrier:
          mergedCarrier,

        files:
          mergedFiles,
      }
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    carrierId,
    normalizedPrefill,
    cacheInit,
  ]);

  /* =======================================================
     Active section scroll spy
     ======================================================= */

  useEffect(() => {
    if (
      !bootstrapped
    ) {
      return undefined;
    }

    const sectionKeys = [
      "basic",
      "contacts",
      "documents",
      "billing",
    ];

    let animationFrameId =
      null;

    const updateActiveSection =
      () => {
        animationFrameId =
          null;

        /*
         * Linha virtual no viewport.
         *
         * Quando o topo de uma secção passa
         * esta linha, essa secção passa a
         * ser considerada ativa.
         */
        const activationLine =
          190;

        let nextActive =
          "basic";

        for (
          let index = 0;
          index <
          sectionKeys.length;
          index += 1
        ) {
          const key =
            sectionKeys[index];

          const section =
            sectionRefs.current[
              key
            ];

          if (
            !section
          ) {
            continue;
          }

          const rect =
            section.getBoundingClientRect();

          if (
            rect.top <=
            activationLine
          ) {
            nextActive =
              key;
          } else {
            /*
             * As secções estão por ordem
             * vertical.
             *
             * Assim que encontramos a
             * primeira cujo topo ainda
             * não passou a linha de
             * ativação, podemos parar.
             */
            break;
          }
        }

        setActiveSection(
          (
            current
          ) =>
            current ===
            nextActive
              ? current
              : nextActive
        );
      };

    const scheduleUpdate =
      () => {
        if (
          animationFrameId !==
          null
        ) {
          return;
        }

        animationFrameId =
          window.requestAnimationFrame(
            updateActiveSection
          );
      };

    /*
     * O LOADEvo pode fazer scroll num
     * container interno em vez do window.
     *
     * Com capture=true apanhamos também
     * esses eventos de scroll.
     */
    document.addEventListener(
      "scroll",
      scheduleUpdate,
      true
    );

    window.addEventListener(
      "resize",
      scheduleUpdate
    );

    /*
     * Determina imediatamente a secção
     * correta assim que os dados ficam
     * disponíveis.
     */
    updateActiveSection();

    return () => {
      document.removeEventListener(
        "scroll",
        scheduleUpdate,
        true
      );

      window.removeEventListener(
        "resize",
        scheduleUpdate
      );

      if (
        animationFrameId !==
        null
      ) {
        window.cancelAnimationFrame(
          animationFrameId
        );
      }
    };
  }, [bootstrapped]);

  const scrollToSection =
    (sectionKey) => {
      setActiveSection(
        sectionKey
      );

      sectionRefs.current[
        sectionKey
      ]?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    };

  /* =======================================================
     Field handlers
     ======================================================= */

  const clearFieldError =
    (fieldName) => {
      setErrors(
        (previous) => {
          if (
            !previous[
              fieldName
            ]
          ) {
            return previous;
          }

          const updated = {
            ...previous,
          };

          delete updated[
            fieldName
          ];

          return updated;
        }
      );
    };

  const handleInputChange =
    (event) => {
      const {
        name,
        value,
      } = event.target;

      dirtyRef.current =
        true;

      setCarrier(
        (previous) => {
          const next = {
            ...previous,
            [name]: value,
          };

          scheduleCacheWrite(
            next,
            files
          );

          return next;
        }
      );

      setErrors(
        (previous) => {
          const updated = {
            ...previous,
          };

          if (
            name === "iban"
          ) {
            if (
              !String(
                value
              ).trim()
            ) {
              delete updated.iban;
            } else if (
              countOnlyAlphaNumeric(
                value
              ) < 15
            ) {
              updated.iban =
                t(
                  "errors.iban_min"
                );
            } else {
              delete updated.iban;
            }

            return updated;
          }

          if (
            name ===
            "swift_bic"
          ) {
            if (
              !String(
                value
              ).trim()
            ) {
              delete updated.swift_bic;
            } else if (
              countOnlyAlphaNumeric(
                value
              ) < 8
            ) {
              updated.swift_bic =
                t(
                  "errors.swift_min"
                );
            } else {
              delete updated.swift_bic;
            }

            return updated;
          }

          if (
            name === "iva"
          ) {
            const normalizedIva =
              String(value)
                .trim()
                .replace(
                  ",",
                  "."
                );

            const parsedIva =
              Number(
                normalizedIva
              );

            if (
              !String(
                value
              ).trim()
            ) {
              delete updated.iva;
            } else if (
              !Number.isFinite(
                parsedIva
              )
            ) {
              updated.iva =
                t(
                  "errors.iva_invalid",
                  {
                    defaultValue:
                      "O IVA deve ser um número válido.",
                  }
                );
            } else if (
              parsedIva <
                0 ||
              parsedIva >
                100
            ) {
              updated.iva =
                t(
                  "errors.iva_range",
                  {
                    defaultValue:
                      "O IVA deve estar entre 0 e 100.",
                  }
                );
            } else {
              delete updated.iva;
            }

            return updated;
          }

          delete updated[
            name
          ];

          return updated;
        }
      );
    };

  const handleFileChange =
    async (event) => {
      const {
        name:
          inputName,

        files:
          inputFiles,
      } = event.target;

      const file =
        inputFiles?.[0];

      if (!file) {
        return;
      }

      dirtyRef.current =
        true;

      const nextFiles = {
        ...files,
        [inputName]: file,
      };

      setFiles(
        nextFiles
      );

      scheduleCacheWrite(
        carrier,
        nextFiles
      );

      clearFieldError(
        inputName
      );

      try {
        const formData =
          new FormData();

        formData.append(
          inputName,
          file
        );

        await axios.post(
          `/carriers/${carrierId}/upload-documents`,
          formData,
          {
            headers: {
              "Content-Type":
                "multipart/form-data",
            },
          }
        );
      } catch (error) {
        console.error(
          `Error uploading ${inputName}:`,
          error
        );

        setErrorMessage(
          t(
            "feedback.upload_failed",
            {
              field:
                t(
                  `files.${inputName}`,
                  {
                    defaultValue:
                      inputName,
                  }
                ),
            }
          )
        );
      }
    };

  /* =======================================================
     Additional contacts
     ======================================================= */

  const handleAddContact =
    () => {
      dirtyRef.current =
        true;

      setCarrier(
        (previous) => {
          const next = {
            ...previous,

            contacts: [
              ...(previous.contacts ||
                []),

              {
                contactName:
                  "",

                contactTitle:
                  "",

                contactEmail:
                  "",

                phoneNumberCountryCode:
                  "",

                phoneNumber:
                  "",
              },
            ],
          };

          scheduleCacheWrite(
            next,
            files
          );

          return next;
        }
      );
    };

  const handleRemoveContact =
    (index) => {
      dirtyRef.current =
        true;

      setCarrier(
        (previous) => {
          const next = {
            ...previous,

            contacts: (
              previous.contacts ||
              []
            ).filter(
              (
                _,
                contactIndex
              ) =>
                contactIndex !==
                index
            ),
          };

          scheduleCacheWrite(
            next,
            files
          );

          return next;
        }
      );

      setErrors(
        (previous) => {
          const updated = {
            ...previous,
          };

          Object.keys(
            updated
          ).forEach(
            (key) => {
              if (
                key.startsWith(
                  `contacts-${index}-`
                )
              ) {
                delete updated[
                  key
                ];
              }
            }
          );

          return updated;
        }
      );
    };

  const handleContactChange =
    (
      index,
      field,
      value
    ) => {
      dirtyRef.current =
        true;

      setCarrier(
        (previous) => {
          const next = {
            ...previous,

            contacts: (
              previous.contacts ||
              []
            ).map(
              (
                contact,
                contactIndex
              ) =>
                contactIndex ===
                index
                  ? {
                      ...contact,
                      [field]:
                        value,
                    }
                  : contact
            ),
          };

          scheduleCacheWrite(
            next,
            files
          );

          return next;
        }
      );

      setErrors(
        (previous) => {
          const updated = {
            ...previous,
          };

          delete updated[
            `contacts-${index}-${field}`
          ];

          if (
            field ===
            "contactEmail"
          ) {
            Object.keys(
              updated
            ).forEach(
              (key) => {
                if (
                  key.startsWith(
                    "contacts-"
                  ) &&
                  key.endsWith(
                    "-contactEmail"
                  )
                ) {
                  delete updated[
                    key
                  ];
                }
              }
            );
          }

          return updated;
        }
      );
    };

  /* =======================================================
     Validation all sections
     ======================================================= */

  const getSectionFromErrors =
    (
      nextErrors
    ) => {
      const keys =
        Object.keys(
          nextErrors
        );

      if (
        keys.length ===
        0
      ) {
        return null;
      }

      if (
        keys.some(
          (key) =>
            key ===
              "companyName" ||
            key ===
              "fleetSize" ||
            key ===
              "driversEmployed" ||
            key ===
              "truckTypes" ||
            key ===
              "loadCapacityPerTruck" ||
            key ===
              "serviceAreas" ||
            key ===
              "servicesOffered" ||
            key ===
              "partnershipHistory"
        )
      ) {
        return "basic";
      }

      if (
        keys.some(
          (key) =>
            key.startsWith(
              "contacts-"
            ) ||
            key ===
              "address" ||
            key ===
              "city" ||
            key ===
              "postalCode" ||
            key ===
              "country" ||
            key ===
              "mainContactName" ||
            key ===
              "mainContactTitle" ||
            key ===
              "contactEmail" ||
            key ===
              "phoneNumberCountryCode" ||
            key ===
              "phoneNumber"
        )
      ) {
        return "contacts";
      }

      if (
        keys.some(
          (key) =>
            key ===
              "communityLicense" ||
            key ===
              "communityLicenseExpiry" ||
            key ===
              "cmrInsurance" ||
            key ===
              "cmrInsuranceExpiry" ||
            key ===
              "motorThirdPartyLiabilityInsurance" ||
            key ===
              "motorThirdPartyLiabilityInsuranceExpiry" ||
            key ===
              "comprehensiveVehicleInsurance" ||
            key ===
              "ComprehensiveVehicleInsuranceExpiryDate"
        )
      ) {
        return "documents";
      }

      return "billing";
    };

  const validateAll =
    () => {
      const nextErrors = {
        ...validateBasic(
          carrier
        ),

        ...validateContacts(
          carrier
        ),

        ...validateDocuments(
          files
        ),

        ...validateBilling(
          carrier
        ),
      };

      setErrors(
        nextErrors
      );

      const firstSection =
        getSectionFromErrors(
          nextErrors
        );

      if (
        firstSection
      ) {
        setTimeout(() => {
          scrollToSection(
            firstSection
          );
        }, 0);
      }

      return (
        Object.keys(
          nextErrors
        ).length ===
        0
      );
    };

  /* =======================================================
     Submit
     ======================================================= */

  const handleSubmit =
    async (event) => {
      event.preventDefault();

      setErrorMessage(
        ""
      );

      if (
        !validateAll()
      ) {
        return;
      }

      setIsSaving(true);

      try {
        const payload = {
          ...carrier,
        };

        if (
          payload.iva !==
            "" &&
          payload.iva !=
            null
        ) {
          const normalizedIva =
            String(
              payload.iva
            )
              .trim()
              .replace(
                ",",
                "."
              );

          const parsedIva =
            Number(
              normalizedIva
            );

          if (
            !Number.isFinite(
              parsedIva
            ) ||
            parsedIva <
              0 ||
            parsedIva >
              100
          ) {
            setErrorMessage(
              t(
                "errors.iva_invalid",
                {
                  defaultValue:
                    "O IVA deve ser um número válido entre 0 e 100.",
                }
              )
            );

            scrollToSection(
              "billing"
            );

            setIsSaving(
              false
            );

            return;
          }

          payload.iva =
            parsedIva.toFixed(
              2
            );
        }

        if (
          !payload.website ||
          payload.website.trim() ===
            ""
        ) {
          delete payload.website;
        }

        if (
          payload.additionalNotes
            ?.trim() ===
          ""
        ) {
          payload.additionalNotes =
            null;
        }

        delete payload.communityLicense;
        delete payload.cmrInsurance;
        delete payload.motorThirdPartyLiabilityInsurance;
        delete payload.comprehensiveVehicleInsurance;

        const formData =
          new FormData();

        Object.entries(
          payload
        ).forEach(
          ([
            key,
            value,
          ]) => {
            if (
              key ===
              "contacts"
            ) {
              formData.append(
                key,
                JSON.stringify(
                  value || []
                )
              );
            } else if (
              Array.isArray(
                value
              )
            ) {
              formData.append(
                key,
                value.join(",")
              );
            } else {
              formData.append(
                key,
                value ?? ""
              );
            }
          }
        );

        Object.entries(
          files
        ).forEach(
          ([
            key,
            value,
          ]) => {
            if (
              value &&
              typeof value !==
                "string"
            ) {
              formData.append(
                key,
                value
              );
            }
          }
        );

        await axios.put(
          `/carriers/${carrierId}`,
          formData,
          {
            headers: {
              "Content-Type":
                "multipart/form-data",
            },
          }
        );

        writeCarrierCache(
          carrierId,
          null
        );

        setShowSuccessPopup(
          true
        );

        setTimeout(() => {
          navigate(
            "/clients?view=carriers"
          );
        }, 1200);
      } catch (error) {
        setShowSuccessPopup(
          false
        );

        setIsSaving(
          false
        );

        console.error(
          "Error updating carrier:",
          error
        );

        const backendError =
          error.response
            ?.data
            ?.details
            ?.join(" | ") ||
          error.response
            ?.data
            ?.error ||
          error.response
            ?.data
            ?.message ||
          t(
            "feedback.update_failed"
          );

        setErrorMessage(
          backendError
        );
      }
    };

  /* =======================================================
     Nav
     ======================================================= */

  const navigationItems =
    useMemo(
      () => [
        {
          key: "basic",

          icon:
            <FaTruck />,

          title:
            t(
              "nav.basic"
            ),

          subtitle:
            t(
              "nav.basic_subtitle"
            ),
        },
        {
          key: "contacts",

          icon:
            <FaMapMarkerAlt />,

          title:
            t(
              "nav.contacts"
            ),

          subtitle:
            t(
              "nav.contacts_subtitle"
            ),
        },
        {
          key:
            "documents",

          icon:
            <FaShieldAlt />,

          title:
            t(
              "nav.documents"
            ),

          subtitle:
            t(
              "nav.documents_subtitle"
            ),
        },
        {
          key: "billing",

          icon:
            <FaCreditCard />,

          title:
            t(
              "nav.billing"
            ),

          subtitle:
            t(
              "nav.billing_subtitle"
            ),
        },
      ],
      [t]
    );

  /* =======================================================
     Document config
     ======================================================= */

  const documentRows =
    [
      {
        fileKey:
          "communityLicense",

        inputId:
          "communityLicense",

        label:
          t(
            "files.communityLicense"
          ),

        expiryName:
          "communityLicenseExpiry",

        expiryValue:
          carrier.communityLicenseExpiry,

        expiryRequired:
          false,

        file:
          files.communityLicense,
      },
      {
        fileKey:
          "cmrInsurance",

        inputId:
          "cmrInsurance",

        label:
          t(
            "files.cmrInsurance"
          ),

        expiryName:
          "cmrInsuranceExpiry",

        expiryValue:
          carrier.cmrInsuranceExpiry,

        expiryRequired:
          false,

        file:
          files.cmrInsurance,
      },
      {
        fileKey:
          "motorThirdPartyLiabilityInsurance",

        inputId:
          "motorThirdPartyLiabilityInsurance",

        label:
          t(
            "files.motorTPL"
          ),

        expiryName:
          "motorThirdPartyLiabilityInsuranceExpiry",

        expiryValue:
          carrier.motorThirdPartyLiabilityInsuranceExpiry,

        expiryRequired:
          true,

        file:
          files.motorThirdPartyLiabilityInsurance,
      },
      {
        fileKey:
          "comprehensiveVehicleInsurance",

        inputId:
          "comprehensiveVehicleInsurance",

        label:
          t(
            "files.comprehensive"
          ),

        expiryName:
          "ComprehensiveVehicleInsuranceExpiryDate",

        expiryValue:
          carrier.ComprehensiveVehicleInsuranceExpiryDate,

        expiryRequired:
          true,

        file:
          files.comprehensiveVehicleInsurance,
      },
    ];

  /* =======================================================
     Loading
     ======================================================= */

  if (
    !bootstrapped
  ) {
    return (
      <div className="edit-carrier-page">
        <div className="edit-carrier-loading">
          <span className="edit-carrier-loading__spinner" />

          <span>
            {t(
              "common.loading",
              {
                defaultValue:
                  "A carregar...",
              }
            )}
          </span>
        </div>
      </div>
    );
  }

  /* =======================================================
     Render
     ======================================================= */

  return (
    <div className="edit-carrier-page">
      {/* =========================
          Success popup
          ========================= */}
      {showSuccessPopup && (
        <div className="edit-carrier-successPopup-overlay">
          <div className="edit-carrier-successPopup">
            <div className="edit-carrier-successPopup-icon">
              ✓
            </div>

            <h3>
              {t(
                "success.updatedTitle",
                {
                  defaultValue:
                    "Transportadora atualizada",
                }
              )}
            </h3>

            <p>
              {t(
                "feedback.update_success",
                {
                  defaultValue:
                    "Transportadora atualizada com sucesso.",
                }
              )}
            </p>

            <button
              type="button"
              onClick={() =>
                navigate(
                  "/clients?view=carriers"
                )
              }
              className="edit-carrier-successPopup-btn"
            >
              {t(
                "actions.ok",
                {
                  defaultValue:
                    "OK",
                }
              )}
            </button>
          </div>
        </div>
      )}

      {/* =========================
          Header
          ========================= */}
      <header className="edit-carrier-header">
        <div className="edit-carrier-header__left">
          <button
            type="button"
            className="edit-carrier-back-button"
            onClick={() =>
              navigate(
                "/clients?view=carriers"
              )
            }
            aria-label={t(
              "actions.back",
              {
                defaultValue:
                  "Voltar",
              }
            )}
          >
            <FaArrowLeft />
          </button>

          <div className="edit-carrier-header__copy">
            <h1>
              {t(
                "header.title",
                {
                  defaultValue:
                    "Editar Transportadora",
                }
              )}
            </h1>

            <p>
              {carrier.companyName ||
                t(
                  "header.company_fallback",
                  {
                    defaultValue:
                      "Transportadora",
                  }
                )}
            </p>
          </div>
        </div>

        <div className="edit-carrier-header__actions">
          <button
            type="button"
            className="edit-carrier-header-cancel"
            onClick={() =>
              navigate(
                "/clients?view=carriers"
              )
            }
          >
            {t(
              "actions.cancel",
              {
                defaultValue:
                  "Cancelar",
              }
            )}
          </button>

          <button
            type="submit"
            form="edit-carrier-main-form"
            className="edit-carrier-header-save"
            disabled={isSaving}
          >
            <FaSave />

            <span>
              {isSaving
                ? t(
                    "actions.saving",
                    {
                      defaultValue:
                        "A guardar...",
                    }
                  )
                : t(
                    "actions.save_changes",
                    {
                      defaultValue:
                        "Guardar alterações",
                    }
                  )}
            </span>
          </button>
        </div>
      </header>

      {/* =========================
          Global error
          ========================= */}
      {errorMessage && (
        <div className="edit-carrier-global-error">
          {errorMessage}
        </div>
      )}

      {/* =========================
          Main layout
          ========================= */}
      <div className="edit-carrier-layout">
        {/* =======================
            Local section nav
            ======================= */}
        <aside className="edit-carrier-section-nav">
          <div className="edit-carrier-section-nav__inner">
            {navigationItems.map(
              (item) => (
                <button
                  type="button"
                  key={
                    item.key
                  }
                  className={`edit-carrier-section-nav__item ${
                    activeSection ===
                    item.key
                      ? "is-active"
                      : ""
                  }`}
                  onClick={() =>
                    scrollToSection(
                      item.key
                    )
                  }
                >
                  <span className="edit-carrier-section-nav__icon">
                    {
                      item.icon
                    }
                  </span>

                  <span className="edit-carrier-section-nav__copy">
                    <strong>
                      {
                        item.title
                      }
                    </strong>

                    <small>
                      {
                        item.subtitle
                      }
                    </small>
                  </span>
                </button>
              )
            )}
          </div>
        </aside>

        {/* =======================
            Form
            ======================= */}
        <form
          id="edit-carrier-main-form"
          onSubmit={
            handleSubmit
          }
          className="edit-carrier-form"
        >
          {/* =========================================
              BASIC INFORMATION
              ========================================= */}
          <section
            ref={(node) => {
              sectionRefs.current.basic =
                node;
            }}
            data-section="basic"
            className="edit-carrier-section"
          >
            <div className="edit-carrier-section-header">
              <span className="edit-carrier-section-header__icon">
                <FaTruck />
              </span>

              <div>
                <h2>
                  {t(
                    "nav.basic"
                  )}
                </h2>

                <p>
                  {t(
                    "section_subtitles.basic"
                  )}
                </p>
              </div>
            </div>

            <div className="edit-carrier-grid edit-carrier-grid--2 edit-carrier-grid--basic-top">
              <div className="edit-carrier-field edit-carrier-field--wide">
                <label htmlFor="companyName">
                  {t(
                    "fields.companyName"
                  )}
                  *
                </label>

                <input
                  type="text"
                  id="companyName"
                  name="companyName"
                  placeholder={t(
                    "placeholders.companyName"
                  )}
                  value={
                    carrier.companyName
                  }
                  onChange={
                    handleInputChange
                  }
                  className={`edit-carrier-input ${
                    errors.companyName
                      ? "edit-carrier-input-error"
                      : ""
                  }`}
                  required
                />

                {errors.companyName && (
                  <p className="edit-carrier-field-error">
                    {
                      errors.companyName
                    }
                  </p>
                )}
              </div>

              <div className="edit-carrier-field">
                <label htmlFor="fleetSize">
                  {t(
                    "fields.fleetSize"
                  )}
                </label>

                <input
                  type="number"
                  id="fleetSize"
                  name="fleetSize"
                  placeholder={t(
                    "placeholders.fleetSize"
                  )}
                  value={
                    carrier.fleetSize
                  }
                  onChange={
                    handleInputChange
                  }
                  className={`edit-carrier-input ${
                    errors.fleetSize
                      ? "edit-carrier-input-error"
                      : ""
                  }`}
                  min="0"
                />

                {errors.fleetSize && (
                  <p className="edit-carrier-field-error">
                    {
                      errors.fleetSize
                    }
                  </p>
                )}
              </div>
            </div>

            <div className="edit-carrier-grid edit-carrier-grid--3">
              <div className="edit-carrier-field">
                <label htmlFor="driversEmployed">
                  {t(
                    "fields.driversEmployed"
                  )}
                </label>

                <input
                  type="number"
                  id="driversEmployed"
                  name="driversEmployed"
                  value={
                    carrier.driversEmployed
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder={t(
                    "placeholders.driversEmployed"
                  )}
                  className={`edit-carrier-input ${
                    errors.driversEmployed
                      ? "edit-carrier-input-error"
                      : ""
                  }`}
                  min="0"
                />

                {errors.driversEmployed && (
                  <p className="edit-carrier-field-error">
                    {
                      errors.driversEmployed
                    }
                  </p>
                )}
              </div>

              <div className="edit-carrier-field">
                <label>
                  {t(
                    "fields.truckTypes"
                  )}
                </label>

                <Select
                  options={
                    truckTypeOptions
                  }
                  isMulti
                  value={(
                    carrier.truckTypes ||
                    []
                  ).map(
                    (value) => ({
                      value,

                      label:
                        t(
                          `truck_types.${value}`
                        ),
                    })
                  )}
                  onChange={(
                    selected
                  ) => {
                    dirtyRef.current =
                      true;

                    setCarrier(
                      (
                        previous
                      ) => {
                        const next =
                          {
                            ...previous,

                            truckTypes:
                              selected
                                ? selected.map(
                                    (
                                      option
                                    ) =>
                                      option.value
                                  )
                                : [],
                          };

                        scheduleCacheWrite(
                          next,
                          files
                        );

                        return next;
                      }
                    );

                    clearFieldError(
                      "truckTypes"
                    );
                  }}
                  placeholder={t(
                    "placeholders.truckTypes"
                  )}
                  isClearable
                  styles={
                    multiInlineScrollStyles
                  }
                  classNamePrefix={
                    errors.truckTypes
                      ? "edit-carrier-select-error"
                      : "edit-carrier-select"
                  }
                />

                {errors.truckTypes && (
                  <p className="edit-carrier-dropdown-field-error">
                    {
                      errors.truckTypes
                    }
                  </p>
                )}
              </div>

              <div className="edit-carrier-field">
                <label htmlFor="loadCapacityPerTruck">
                  {t(
                    "fields.loadCapacityPerTruck"
                  )}
                </label>

                <input
                  type="number"
                  id="loadCapacityPerTruck"
                  name="loadCapacityPerTruck"
                  value={
                    carrier.loadCapacityPerTruck
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder={t(
                    "placeholders.loadCapacityPerTruck"
                  )}
                  className={`edit-carrier-input ${
                    errors.loadCapacityPerTruck
                      ? "edit-carrier-input-error"
                      : ""
                  }`}
                  min="0"
                  step="0.01"
                />

                {errors.loadCapacityPerTruck && (
                  <p className="edit-carrier-field-error">
                    {
                      errors.loadCapacityPerTruck
                    }
                  </p>
                )}
              </div>
            </div>

            <div className="edit-carrier-grid edit-carrier-grid--3">
              <div className="edit-carrier-field">
                <label>
                  {t(
                    "fields.serviceAreas"
                  )}
                </label>

                <Select
                  options={[
                    {
                      label:
                        t(
                          "groups.europe"
                        ),

                      options:
                        europeCountries,
                    },
                    {
                      label:
                        t(
                          "groups.usa"
                        ),

                      options:
                        usaStates,
                    },
                  ]}
                  isMulti
                  value={(
                    carrier.serviceAreas ||
                    []
                  ).map(
                    (
                      area
                    ) => ({
                      value:
                        area,

                      label:
                        t(
                          `countries_list.${area}`,
                          {
                            defaultValue:
                              t(
                                `usa_states.${area}`,
                                {
                                  defaultValue:
                                    area,
                                }
                              ),
                          }
                        ),
                    })
                  )}
                  onChange={(
                    selected
                  ) => {
                    dirtyRef.current =
                      true;

                    setCarrier(
                      (
                        previous
                      ) => {
                        const next =
                          {
                            ...previous,

                            serviceAreas:
                              selected
                                ? selected.map(
                                    (
                                      option
                                    ) =>
                                      option.value
                                  )
                                : [],
                          };

                        scheduleCacheWrite(
                          next,
                          files
                        );

                        return next;
                      }
                    );

                    clearFieldError(
                      "serviceAreas"
                    );
                  }}
                  placeholder={t(
                    "placeholders.serviceAreas"
                  )}
                  isClearable
                  styles={
                    multiInlineScrollStyles
                  }
                  classNamePrefix={
                    errors.serviceAreas
                      ? "edit-carrier-select-error"
                      : "edit-carrier-select"
                  }
                />

                {errors.serviceAreas && (
                  <p className="edit-carrier-dropdown-field-error">
                    {
                      errors.serviceAreas
                    }
                  </p>
                )}
              </div>

              <div className="edit-carrier-field">
                <label>
                  {t(
                    "fields.servicesOffered"
                  )}
                </label>

                <Select
                  options={
                    servicesOfferedOptions
                  }
                  isMulti
                  value={(
                    carrier.servicesOffered ||
                    []
                  ).map(
                    (
                      service
                    ) => ({
                      value:
                        service,

                      label:
                        t(
                          `services.${service}`
                        ),
                    })
                  )}
                  onChange={(
                    selected
                  ) => {
                    dirtyRef.current =
                      true;

                    setCarrier(
                      (
                        previous
                      ) => {
                        const next =
                          {
                            ...previous,

                            servicesOffered:
                              selected
                                ? selected.map(
                                    (
                                      option
                                    ) =>
                                      option.value
                                  )
                                : [],
                          };

                        scheduleCacheWrite(
                          next,
                          files
                        );

                        return next;
                      }
                    );

                    clearFieldError(
                      "servicesOffered"
                    );
                  }}
                  placeholder={t(
                    "placeholders.servicesOffered"
                  )}
                  isClearable
                  styles={
                    multiInlineScrollStyles
                  }
                  classNamePrefix={
                    errors.servicesOffered
                      ? "edit-carrier-select-error"
                      : "edit-carrier-select"
                  }
                />

                {errors.servicesOffered && (
                  <p className="edit-carrier-dropdown-field-error">
                    {
                      errors.servicesOffered
                    }
                  </p>
                )}
              </div>

              <div className="edit-carrier-field">
                <label>
                  {t(
                    "fields.partnershipHistory"
                  )}
                </label>

                <Select
                  options={
                    partnershipOptions
                  }
                  value={
                    partnershipOptions.find(
                      (
                        option
                      ) =>
                        option.value ===
                        carrier.partnershipHistory
                    ) ||
                    null
                  }
                  onChange={(
                    option
                  ) => {
                    dirtyRef.current =
                      true;

                    setCarrier(
                      (
                        previous
                      ) => {
                        const next =
                          {
                            ...previous,

                            partnershipHistory:
                              option
                                ? option.value
                                : "",
                          };

                        scheduleCacheWrite(
                          next,
                          files
                        );

                        return next;
                      }
                    );

                    clearFieldError(
                      "partnershipHistory"
                    );
                  }}
                  placeholder={t(
                    "placeholders.partnershipHistory"
                  )}
                  isClearable
                  isSearchable={
                    false
                  }
                  styles={
                    customSelectStyles
                  }
                  formatOptionLabel={(
                    option
                  ) => (
                    <div className="edit-carrier-option-with-icon">
                      {
                        option.icon
                      }

                      <span>
                        {
                          option.label
                        }
                      </span>
                    </div>
                  )}
                  classNamePrefix={
                    errors.partnershipHistory
                      ? "edit-carrier-select-error"
                      : "edit-carrier-select"
                  }
                />

                {errors.partnershipHistory && (
                  <p className="edit-carrier-dropdown-field-error">
                    {
                      errors.partnershipHistory
                    }
                  </p>
                )}
              </div>
            </div>
          </section>

          {/* =========================================
              ADDRESS & CONTACTS
              ========================================= */}
          <section
            ref={(node) => {
              sectionRefs.current.contacts =
                node;
            }}
            data-section="contacts"
            className="edit-carrier-section"
          >
            <div className="edit-carrier-section-header">
              <span className="edit-carrier-section-header__icon">
                <FaMapMarkerAlt />
              </span>

              <div>
                <h2>
                  {t(
                    "nav.contacts"
                  )}
                </h2>

                <p>
                  {t(
                    "section_subtitles.contacts"
                  )}
                </p>
              </div>
            </div>

            <div className="edit-carrier-grid edit-carrier-grid--1">
              <div className="edit-carrier-field">
                <label htmlFor="address">
                  {t(
                    "fields.address"
                  )}
                </label>

                <input
                  type="text"
                  id="address"
                  name="address"
                  value={
                    carrier.address
                  }
                  onChange={
                    handleInputChange
                  }
                  className={`edit-carrier-input ${
                    errors.address
                      ? "edit-carrier-input-error"
                      : ""
                  }`}
                />

                {errors.address && (
                  <p className="edit-carrier-field-error">
                    {
                      errors.address
                    }
                  </p>
                )}
              </div>
            </div>

            <div className="edit-carrier-grid edit-carrier-grid--3">
              <div className="edit-carrier-field">
                <label htmlFor="city">
                  {t(
                    "fields.city"
                  )}
                </label>

                <input
                  type="text"
                  id="city"
                  name="city"
                  value={
                    carrier.city
                  }
                  onChange={
                    handleInputChange
                  }
                  className={`edit-carrier-input ${
                    errors.city
                      ? "edit-carrier-input-error"
                      : ""
                  }`}
                />

                {errors.city && (
                  <p className="edit-carrier-field-error">
                    {
                      errors.city
                    }
                  </p>
                )}
              </div>

              <div className="edit-carrier-field">
                <label htmlFor="postalCode">
                  {t(
                    "fields.postalCode"
                  )}
                </label>

                <input
                  type="text"
                  id="postalCode"
                  name="postalCode"
                  value={
                    carrier.postalCode
                  }
                  onChange={
                    handleInputChange
                  }
                  className={`edit-carrier-input ${
                    errors.postalCode
                      ? "edit-carrier-input-error"
                      : ""
                  }`}
                />

                {errors.postalCode && (
                  <p className="edit-carrier-field-error">
                    {
                      errors.postalCode
                    }
                  </p>
                )}
              </div>

              <div className="edit-carrier-field">
                <label>
                  {t(
                    "fields.country"
                  )}
                </label>

                <Select
                  options={
                    mapCountryOpts
                  }
                  value={
                    mapCountryOpts.find(
                      (
                        option
                      ) =>
                        option.value ===
                        carrier.country
                    ) ||
                    null
                  }
                  onChange={(
                    option
                  ) => {
                    dirtyRef.current =
                      true;

                    setCarrier(
                      (
                        previous
                      ) => {
                        const next =
                          {
                            ...previous,

                            country:
                              option
                                ? option.value
                                : "",
                          };

                        scheduleCacheWrite(
                          next,
                          files
                        );

                        return next;
                      }
                    );

                    clearFieldError(
                      "country"
                    );
                  }}
                  placeholder={t(
                    "placeholders.country"
                  )}
                  formatOptionLabel={(
                    country
                  ) => (
                    <div className="edit-carrier-option-with-icon">
                      <img
                        src={`https://flagcdn.com/w40/${country.code}.png`}
                        alt={
                          country.label
                        }
                        className="edit-carrier-option-flag"
                      />

                      <span>
                        {
                          country.label
                        }
                      </span>
                    </div>
                  )}
                  isClearable
                  styles={
                    customSelectStyles
                  }
                  classNamePrefix={
                    errors.country
                      ? "edit-carrier-select-error"
                      : "edit-carrier-select"
                  }
                />

                {errors.country && (
                  <p className="edit-carrier-dropdown-field-error">
                    {
                      errors.country
                    }
                  </p>
                )}
              </div>
            </div>

            <div className="edit-carrier-grid edit-carrier-grid--1">
              <div className="edit-carrier-field">
                <label htmlFor="website">
                  {t(
                    "fields.website"
                  )}
                </label>

                <input
                  type="url"
                  id="website"
                  name="website"
                  value={
                    carrier.website ||
                    ""
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder={t(
                    "placeholders.website"
                  )}
                  className={`edit-carrier-input ${
                    errors.website
                      ? "edit-carrier-input-error"
                      : ""
                  }`}
                />

                {errors.website && (
                  <p className="edit-carrier-field-error">
                    {
                      errors.website
                    }
                  </p>
                )}
              </div>
            </div>

            <div className="edit-carrier-grid edit-carrier-grid--2">
              <div className="edit-carrier-field">
                <label htmlFor="mainContactName">
                  {t(
                    "fields.mainContactName"
                  )}
                </label>

                <input
                  type="text"
                  id="mainContactName"
                  name="mainContactName"
                  value={
                    carrier.mainContactName
                  }
                  onChange={
                    handleInputChange
                  }
                  className={`edit-carrier-input ${
                    errors.mainContactName
                      ? "edit-carrier-input-error"
                      : ""
                  }`}
                />

                {errors.mainContactName && (
                  <p className="edit-carrier-field-error">
                    {
                      errors.mainContactName
                    }
                  </p>
                )}
              </div>

              <div className="edit-carrier-field">
                <label htmlFor="mainContactTitle">
                  {t(
                    "fields.mainContactTitle"
                  )}
                </label>

                <input
                  type="text"
                  id="mainContactTitle"
                  name="mainContactTitle"
                  value={
                    carrier.mainContactTitle
                  }
                  onChange={
                    handleInputChange
                  }
                  className={`edit-carrier-input ${
                    errors.mainContactTitle
                      ? "edit-carrier-input-error"
                      : ""
                  }`}
                />

                {errors.mainContactTitle && (
                  <p className="edit-carrier-field-error">
                    {
                      errors.mainContactTitle
                    }
                  </p>
                )}
              </div>
            </div>

            <div className="edit-carrier-grid edit-carrier-grid--3">
              <div className="edit-carrier-field">
                <label htmlFor="contactEmail">
                  {t(
                    "fields.contactEmail"
                  )}
                </label>

                <input
                  type="email"
                  id="contactEmail"
                  name="contactEmail"
                  value={
                    carrier.contactEmail
                  }
                  onChange={
                    handleInputChange
                  }
                  className={`edit-carrier-input ${
                    errors.contactEmail
                      ? "edit-carrier-input-error"
                      : ""
                  }`}
                />

                {errors.contactEmail && (
                  <p className="edit-carrier-field-error">
                    {
                      errors.contactEmail
                    }
                  </p>
                )}
              </div>

              <div className="edit-carrier-field">
                <label>
                  {t(
                    "fields.phoneNumberCountryCode"
                  )}
                </label>

                <Select
                  options={
                    mapPhoneOpts
                  }
                  value={
                    mapPhoneOpts.find(
                      (
                        option
                      ) =>
                        option.value ===
                        carrier.phoneNumberCountryCode
                    ) ||
                    null
                  }
                  onChange={(
                    option
                  ) => {
                    dirtyRef.current =
                      true;

                    setCarrier(
                      (
                        previous
                      ) => {
                        const next =
                          {
                            ...previous,

                            phoneNumberCountryCode:
                              option
                                ? option.value
                                : "",
                          };

                        scheduleCacheWrite(
                          next,
                          files
                        );

                        return next;
                      }
                    );

                    clearFieldError(
                      "phoneNumberCountryCode"
                    );
                  }}
                  placeholder={t(
                    "placeholders.phone_code"
                  )}
                  formatOptionLabel={(
                    option
                  ) => (
                    <div className="edit-carrier-option-with-icon">
                      <img
                        src={`https://flagcdn.com/w40/${option.code}.png`}
                        alt={
                          option.label
                        }
                        className="edit-carrier-option-flag"
                      />

                      <span>
                        {
                          option.label
                        }
                      </span>
                    </div>
                  )}
                  isClearable
                  styles={
                    customSelectStyles
                  }
                  classNamePrefix={
                    errors.phoneNumberCountryCode
                      ? "edit-carrier-select-error"
                      : "edit-carrier-select"
                  }
                />

                {errors.phoneNumberCountryCode && (
                  <p className="edit-carrier-dropdown-field-error">
                    {
                      errors.phoneNumberCountryCode
                    }
                  </p>
                )}
              </div>

              <div className="edit-carrier-field">
                <label htmlFor="phoneNumber">
                  {t(
                    "fields.phoneNumber"
                  )}
                </label>

                <input
                  type="text"
                  id="phoneNumber"
                  name="phoneNumber"
                  value={
                    carrier.phoneNumber
                  }
                  onChange={
                    handleInputChange
                  }
                  className={`edit-carrier-input ${
                    errors.phoneNumber
                      ? "edit-carrier-input-error"
                      : ""
                  }`}
                />

                {errors.phoneNumber && (
                  <p className="edit-carrier-field-error">
                    {
                      errors.phoneNumber
                    }
                  </p>
                )}
              </div>
            </div>

            {/* Additional contacts */}
            <div className="edit-carrier-subsection-header">
              <div>
                <h3>
                  {t(
                    "contacts.section_title",
                    {
                      defaultValue:
                        "Contactos Adicionais",
                    }
                  )}
                </h3>

                <p>
                  {t(
                    "contacts.section_subtitle",
                    {
                      defaultValue:
                        "Adicione outros contactos relevantes desta transportadora.",
                    }
                  )}
                </p>
              </div>

              <button
                type="button"
                className="edit-carrier-add-user-button"
                onClick={
                  handleAddContact
                }
              >
                <FaPlus />

                <span>
                  {t(
                    "contacts.actions.add",
                    {
                      defaultValue:
                        "Adicionar contacto",
                    }
                  )}
                </span>
              </button>
            </div>

            {(carrier.contacts ||
              []).length ===
            0 ? (
              <div className="edit-carrier-empty-contacts">
                {t(
                  "contacts.empty",
                  {
                    defaultValue:
                      "Não existem contactos adicionais.",
                  }
                )}
              </div>
            ) : (
              <div className="edit-carrier-extra-contacts">
                {(
                  carrier.contacts ||
                  []
                ).map(
                  (
                    contact,
                    index
                  ) => (
                    <div
                      key={
                        index
                      }
                      className="edit-carrier-extra-contact-block"
                    >
                      <div className="edit-carrier-extra-contact-heading">
                        <strong>
                          {t(
                            "contacts.additional_title",
                            {
                              number:
                                index +
                                1,

                              defaultValue:
                                "Contacto adicional {{number}}",
                            }
                          )}
                        </strong>

                        <button
                          type="button"
                          className="edit-carrier-remove-user-button"
                          onClick={() =>
                            handleRemoveContact(
                              index
                            )
                          }
                          title={t(
                            "contacts.actions.remove"
                          )}
                          aria-label={t(
                            "contacts.actions.remove"
                          )}
                        >
                          <FaTrashAlt />
                        </button>
                      </div>

                      <div className="edit-carrier-extra-contact-row">
                        <div className="edit-carrier-field">
                          <label>
                            {t(
                              "contacts.fields.name"
                            )}
                          </label>

                          <input
                            type="text"
                            value={
                              contact.contactName ||
                              ""
                            }
                            onChange={(
                              event
                            ) =>
                              handleContactChange(
                                index,
                                "contactName",
                                event
                                  .target
                                  .value
                              )
                            }
                            className={`edit-carrier-input ${
                              errors[
                                `contacts-${index}-contactName`
                              ]
                                ? "edit-carrier-input-error"
                                : ""
                            }`}
                            placeholder={t(
                              "contacts.placeholders.name"
                            )}
                          />

                          {errors[
                            `contacts-${index}-contactName`
                          ] && (
                            <p className="edit-carrier-field-error">
                              {
                                errors[
                                  `contacts-${index}-contactName`
                                ]
                              }
                            </p>
                          )}
                        </div>

                        <div className="edit-carrier-field">
                          <label>
                            {t(
                              "contacts.fields.title"
                            )}
                          </label>

                          <input
                            type="text"
                            value={
                              contact.contactTitle ||
                              ""
                            }
                            onChange={(
                              event
                            ) =>
                              handleContactChange(
                                index,
                                "contactTitle",
                                event
                                  .target
                                  .value
                              )
                            }
                            className={`edit-carrier-input ${
                              errors[
                                `contacts-${index}-contactTitle`
                              ]
                                ? "edit-carrier-input-error"
                                : ""
                            }`}
                            placeholder={t(
                              "contacts.placeholders.title"
                            )}
                          />

                          {errors[
                            `contacts-${index}-contactTitle`
                          ] && (
                            <p className="edit-carrier-field-error">
                              {
                                errors[
                                  `contacts-${index}-contactTitle`
                                ]
                              }
                            </p>
                          )}
                        </div>

                        <div className="edit-carrier-field">
                          <label>
                            {t(
                              "contacts.fields.email"
                            )}
                          </label>

                          <input
                            type="email"
                            value={
                              contact.contactEmail ||
                              ""
                            }
                            onChange={(
                              event
                            ) =>
                              handleContactChange(
                                index,
                                "contactEmail",
                                event
                                  .target
                                  .value
                              )
                            }
                            className={`edit-carrier-input ${
                              errors[
                                `contacts-${index}-contactEmail`
                              ]
                                ? "edit-carrier-input-error"
                                : ""
                            }`}
                            placeholder={t(
                              "contacts.placeholders.email"
                            )}
                          />

                          {errors[
                            `contacts-${index}-contactEmail`
                          ] && (
                            <p className="edit-carrier-field-error">
                              {
                                errors[
                                  `contacts-${index}-contactEmail`
                                ]
                              }
                            </p>
                          )}
                        </div>

                        <div className="edit-carrier-field">
                          <label>
                            {t(
                              "contacts.fields.countryCode"
                            )}
                          </label>

                          <Select
                            options={
                              mapPhoneOpts
                            }
                            value={
                              mapPhoneOpts.find(
                                (
                                  option
                                ) =>
                                  option.value ===
                                  contact.phoneNumberCountryCode
                              ) ||
                              null
                            }
                            onChange={(
                              option
                            ) =>
                              handleContactChange(
                                index,
                                "phoneNumberCountryCode",
                                option
                                  ? option.value
                                  : ""
                              )
                            }
                            placeholder={t(
                              "contacts.placeholders.countryCode"
                            )}
                            formatOptionLabel={(
                              option
                            ) => (
                              <div className="edit-carrier-option-with-icon">
                                <img
                                  src={`https://flagcdn.com/w40/${option.code}.png`}
                                  alt={
                                    option.label
                                  }
                                  className="edit-carrier-option-flag"
                                />

                                <span>
                                  {
                                    option.label
                                  }
                                </span>
                              </div>
                            )}
                            isClearable
                            styles={
                              customSelectStyles
                            }
                            classNamePrefix={
                              errors[
                                `contacts-${index}-phoneNumberCountryCode`
                              ]
                                ? "edit-carrier-select-error"
                                : "edit-carrier-select"
                            }
                          />

                          {errors[
                            `contacts-${index}-phoneNumberCountryCode`
                          ] && (
                            <p className="edit-carrier-dropdown-field-error">
                              {
                                errors[
                                  `contacts-${index}-phoneNumberCountryCode`
                                ]
                              }
                            </p>
                          )}
                        </div>

                        <div className="edit-carrier-field">
                          <label>
                            {t(
                              "contacts.fields.phoneNumber"
                            )}
                          </label>

                          <input
                            type="text"
                            value={
                              contact.phoneNumber ||
                              ""
                            }
                            onChange={(
                              event
                            ) =>
                              handleContactChange(
                                index,
                                "phoneNumber",
                                event
                                  .target
                                  .value
                              )
                            }
                            className={`edit-carrier-input ${
                              errors[
                                `contacts-${index}-phoneNumber`
                              ]
                                ? "edit-carrier-input-error"
                                : ""
                            }`}
                            placeholder={t(
                              "contacts.placeholders.phoneNumber"
                            )}
                          />

                          {errors[
                            `contacts-${index}-phoneNumber`
                          ] && (
                            <p className="edit-carrier-field-error">
                              {
                                errors[
                                  `contacts-${index}-phoneNumber`
                                ]
                              }
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                )}
              </div>
            )}
          </section>

          {/* =========================================
              DOCUMENTS
              ========================================= */}
          <section
            ref={(node) => {
              sectionRefs.current.documents =
                node;
            }}
            data-section="documents"
            className="edit-carrier-section"
          >
            <div className="edit-carrier-section-header">
              <span className="edit-carrier-section-header__icon">
                <FaShieldAlt />
              </span>

              <div>
                <h2>
                  {t(
                    "nav.documents"
                  )}
                </h2>

                <p>
                  {t(
                    "section_subtitles.documents"
                  )}
                </p>
              </div>
            </div>

            <div className="edit-carrier-documents-table">
              <div className="edit-carrier-documents-header">
                <span>
                  {t(
                    "documents.document",
                    {
                      defaultValue:
                        "Documento",
                    }
                  )}
                </span>

                <span>
                  {t(
                    "documents.action",
                    {
                      defaultValue:
                        "Ação",
                    }
                  )}
                </span>

                <span>
                  {t(
                    "documents.validity",
                    {
                      defaultValue:
                        "Data de Validade",
                    }
                  )}
                </span>
              </div>

              {documentRows.map(
                (
                  document
                ) => (
                  <CarrierDocumentRow
                    key={
                      document.fileKey
                    }
                    {...document}
                    fileError={
                      errors[
                        document.fileKey
                      ]
                    }
                    expiryError={
                      errors[
                        document.expiryName
                      ]
                    }
                    handleFileChange={
                      handleFileChange
                    }
                    handleInputChange={
                      handleInputChange
                    }
                    t={t}
                  />
                )
              )}
            </div>
          </section>

          {/* =========================================
              BANKING / PAYMENT
              ========================================= */}
          <section
            ref={(node) => {
              sectionRefs.current.billing =
                node;
            }}
            data-section="billing"
            className="edit-carrier-section"
          >
            <div className="edit-carrier-section-header">
              <span className="edit-carrier-section-header__icon">
                <FaCreditCard />
              </span>

              <div>
                <h2>
                  {t(
                    "nav.billing"
                  )}
                </h2>

                <p>
                  {t(
                    "section_subtitles.billing"
                  )}
                </p>
              </div>
            </div>

            <div className="edit-carrier-grid edit-carrier-grid--2">
              <div className="edit-carrier-field">
                <label htmlFor="accountHolderName">
                  {t(
                    "fields.accountHolderName"
                  )}
                </label>

                <input
                  type="text"
                  id="accountHolderName"
                  name="accountHolderName"
                  value={
                    carrier.accountHolderName
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder={t(
                    "placeholders.accountHolderName"
                  )}
                  className={`edit-carrier-input ${
                    errors.accountHolderName
                      ? "edit-carrier-input-error"
                      : ""
                  }`}
                />

                {errors.accountHolderName && (
                  <p className="edit-carrier-field-error">
                    {
                      errors.accountHolderName
                    }
                  </p>
                )}
              </div>

              <div className="edit-carrier-field">
                <label htmlFor="accountNumber">
                  {t(
                    "fields.accountNumber"
                  )}
                </label>

                <input
                  type="text"
                  id="accountNumber"
                  name="accountNumber"
                  value={
                    carrier.accountNumber
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder={t(
                    "placeholders.accountNumber"
                  )}
                  className={`edit-carrier-input ${
                    errors.accountNumber
                      ? "edit-carrier-input-error"
                      : ""
                  }`}
                />

                {errors.accountNumber && (
                  <p className="edit-carrier-field-error">
                    {
                      errors.accountNumber
                    }
                  </p>
                )}
              </div>
            </div>

            <div className="edit-carrier-grid edit-carrier-grid--4">
              <div className="edit-carrier-field">
                <label htmlFor="bankName">
                  {t(
                    "fields.bankName"
                  )}
                </label>

                <input
                  type="text"
                  id="bankName"
                  name="bankName"
                  value={
                    carrier.bankName
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder={t(
                    "placeholders.bankName"
                  )}
                  className={`edit-carrier-input ${
                    errors.bankName
                      ? "edit-carrier-input-error"
                      : ""
                  }`}
                />

                {errors.bankName && (
                  <p className="edit-carrier-field-error">
                    {
                      errors.bankName
                    }
                  </p>
                )}
              </div>

              <div className="edit-carrier-field">
                <label htmlFor="iban">
                  {t(
                    "fields.iban"
                  )}
                </label>

                <input
                  type="text"
                  id="iban"
                  name="iban"
                  value={
                    carrier.iban
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder={t(
                    "placeholders.iban"
                  )}
                  className={`edit-carrier-input ${
                    errors.iban
                      ? "edit-carrier-input-error"
                      : ""
                  }`}
                />

                {errors.iban && (
                  <p className="edit-carrier-field-error">
                    {
                      errors.iban
                    }
                  </p>
                )}
              </div>

              <div className="edit-carrier-field">
                <label htmlFor="swift_bic">
                  {t(
                    "fields.swift_bic"
                  )}
                </label>

                <input
                  type="text"
                  id="swift_bic"
                  name="swift_bic"
                  value={
                    carrier.swift_bic
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder={t(
                    "placeholders.swift_bic"
                  )}
                  className={`edit-carrier-input ${
                    errors.swift_bic
                      ? "edit-carrier-input-error"
                      : ""
                  }`}
                />

                {errors.swift_bic && (
                  <p className="edit-carrier-field-error">
                    {
                      errors.swift_bic
                    }
                  </p>
                )}
              </div>

              <div className="edit-carrier-field">
                <label>
                  {t(
                    "fields.currency"
                  )}
                </label>

                <Select
                  options={
                    mapCurrencyOpts
                  }
                  value={
                    mapCurrencyOpts.find(
                      (
                        option
                      ) =>
                        option.value ===
                        carrier.currency
                    ) ||
                    null
                  }
                  onChange={(
                    option
                  ) => {
                    dirtyRef.current =
                      true;

                    setCarrier(
                      (
                        previous
                      ) => {
                        const next =
                          {
                            ...previous,

                            currency:
                              option
                                ? option.value
                                : "",
                          };

                        scheduleCacheWrite(
                          next,
                          files
                        );

                        return next;
                      }
                    );

                    clearFieldError(
                      "currency"
                    );
                  }}
                  placeholder={t(
                    "placeholders.currency"
                  )}
                  formatOptionLabel={(
                    currency
                  ) => (
                    <div className="edit-carrier-option-with-icon">
                      <img
                        src={`https://flagcdn.com/w40/${currency.code}.png`}
                        alt={
                          currency.label
                        }
                        className="edit-carrier-option-flag"
                      />

                      <span>
                        {
                          currency.label
                        }
                      </span>
                    </div>
                  )}
                  isClearable
                  isSearchable={
                    false
                  }
                  styles={
                    customSelectStyles
                  }
                  classNamePrefix="edit-carrier-select"
                />

                {errors.currency && (
                  <p className="edit-carrier-dropdown-field-error">
                    {
                      errors.currency
                    }
                  </p>
                )}
              </div>
            </div>

            <div className="edit-carrier-grid edit-carrier-grid--4">
              <div className="edit-carrier-field">
                <label>
                  {t(
                    "fields.paymentMethodsAccepted"
                  )}
                </label>

                <Select
                  options={
                    paymentOptions
                  }
                  isMulti
                  closeMenuOnSelect={
                    false
                  }
                  hideSelectedOptions={
                    false
                  }
                  value={
                    paymentOptions.filter(
                      (
                        option
                      ) =>
                        (
                          carrier.paymentMethodsAccepted ||
                          []
                        ).includes(
                          option.value
                        )
                    )
                  }
                  onChange={(
                    selected
                  ) => {
                    dirtyRef.current =
                      true;

                    setCarrier(
                      (
                        previous
                      ) => {
                        const next =
                          {
                            ...previous,

                            paymentMethodsAccepted:
                              selected
                                ? selected.map(
                                    (
                                      option
                                    ) =>
                                      option.value
                                  )
                                : [],
                          };

                        scheduleCacheWrite(
                          next,
                          files
                        );

                        return next;
                      }
                    );

                    clearFieldError(
                      "paymentMethodsAccepted"
                    );
                  }}
                  placeholder={t(
                    "placeholders.paymentMethodsAccepted"
                  )}
                  styles={
                    multiInlineScrollStyles
                  }
                  isSearchable={
                    false
                  }
                  classNamePrefix="edit-carrier-select"
                />

                {errors.paymentMethodsAccepted && (
                  <p className="edit-carrier-dropdown-field-error">
                    {
                      errors.paymentMethodsAccepted
                    }
                  </p>
                )}
              </div>

              <div className="edit-carrier-field">
                <label>
                  {t(
                    "fields.paymentDueDate"
                  )}
                </label>

                <Select
                  options={
                    paymentDueDateOptions
                  }
                  value={
                    carrier.paymentDueDate
                      ? {
                          value:
                            carrier.paymentDueDate,

                          label:
                            t(
                              `due_dates.${carrier.paymentDueDate}`
                            ),
                        }
                      : null
                  }
                  onChange={(
                    option
                  ) => {
                    dirtyRef.current =
                      true;

                    setCarrier(
                      (
                        previous
                      ) => {
                        const next =
                          {
                            ...previous,

                            paymentDueDate:
                              option?.value ||
                              "",
                          };

                        scheduleCacheWrite(
                          next,
                          files
                        );

                        return next;
                      }
                    );

                    clearFieldError(
                      "paymentDueDate"
                    );
                  }}
                  placeholder={t(
                    "placeholders.paymentDueDate"
                  )}
                  isClearable
                  isSearchable={
                    false
                  }
                  styles={
                    customSelectStyles
                  }
                  classNamePrefix="edit-carrier-select"
                />

                {errors.paymentDueDate && (
                  <p className="edit-carrier-dropdown-field-error">
                    {
                      errors.paymentDueDate
                    }
                  </p>
                )}
              </div>

              <div className="edit-carrier-field">
                <label>
                  {t(
                    "fields.discountTerms"
                  )}
                </label>

                <Select
                  options={[
                    ...Array(21),
                  ].map(
                    (
                      _,
                      index
                    ) => ({
                      value:
                        `${
                          index *
                          5
                        }%`,

                      label:
                        `${
                          index *
                          5
                        }%`,
                    })
                  )}
                  value={
                    carrier.discountTerms
                      ? {
                          value:
                            carrier.discountTerms,

                          label:
                            carrier.discountTerms,
                        }
                      : null
                  }
                  onChange={(
                    option
                  ) => {
                    dirtyRef.current =
                      true;

                    setCarrier(
                      (
                        previous
                      ) => {
                        const next =
                          {
                            ...previous,

                            discountTerms:
                              option
                                ? option.value
                                : "",
                          };

                        scheduleCacheWrite(
                          next,
                          files
                        );

                        return next;
                      }
                    );

                    clearFieldError(
                      "discountTerms"
                    );
                  }}
                  placeholder={t(
                    "placeholders.discountTerms"
                  )}
                  isClearable
                  isSearchable={
                    false
                  }
                  styles={
                    customSelectStyles
                  }
                  classNamePrefix="edit-carrier-select"
                />

                {errors.discountTerms && (
                  <p className="edit-carrier-dropdown-field-error">
                    {
                      errors.discountTerms
                    }
                  </p>
                )}
              </div>

              <div className="edit-carrier-field">
                <label htmlFor="latePaymentPenalties">
                  {t(
                    "fields.latePaymentPenalties"
                  )}
                </label>

                <input
                  type="text"
                  id="latePaymentPenalties"
                  name="latePaymentPenalties"
                  value={
                    carrier.latePaymentPenalties
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder={t(
                    "placeholders.latePaymentPenalties"
                  )}
                  className={`edit-carrier-input ${
                    errors.latePaymentPenalties
                      ? "edit-carrier-input-error"
                      : ""
                  }`}
                />

                {errors.latePaymentPenalties && (
                  <p className="edit-carrier-field-error">
                    {
                      errors.latePaymentPenalties
                    }
                  </p>
                )}
              </div>
            </div>

            <div className="edit-carrier-section-divider" />

            <div className="edit-carrier-subsection-heading">
              <h3>
                {t(
                  "sections.invoicing_prefs"
                )}
              </h3>

              <p>
                {t(
                  "section_subtitles.invoicing"
                )}
              </p>
            </div>

            <div className="edit-carrier-grid edit-carrier-grid--4">
              <div className="edit-carrier-field">
                <label htmlFor="VAT_TaxInformation">
                  {t(
                    "fields.VAT_TaxInformation"
                  )}
                </label>

                <input
                  type="text"
                  id="VAT_TaxInformation"
                  name="VAT_TaxInformation"
                  value={
                    carrier.VAT_TaxInformation
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder={t(
                    "placeholders.VAT_TaxInformation"
                  )}
                  className={`edit-carrier-input ${
                    errors.VAT_TaxInformation
                      ? "edit-carrier-input-error"
                      : ""
                  }`}
                />

                {errors.VAT_TaxInformation && (
                  <p className="edit-carrier-field-error">
                    {
                      errors.VAT_TaxInformation
                    }
                  </p>
                )}
              </div>

              <div className="edit-carrier-field">
                <label htmlFor="iva">
                  {t(
                    "fields.iva",
                    {
                      defaultValue:
                        "IVA (%)",
                    }
                  )}
                </label>

                <input
                  type="number"
                  id="iva"
                  name="iva"
                  value={
                    carrier.iva ||
                    ""
                  }
                  onChange={
                    handleInputChange
                  }
                  onBlur={() => {
                    const normalizedIva =
                      String(
                        carrier.iva ||
                          ""
                      )
                        .trim()
                        .replace(
                          ",",
                          "."
                        );

                    if (
                      !normalizedIva
                    ) {
                      clearFieldError(
                        "iva"
                      );

                      return;
                    }

                    const parsedIva =
                      Number(
                        normalizedIva
                      );

                    if (
                      !Number.isFinite(
                        parsedIva
                      )
                    ) {
                      setErrors(
                        (
                          previous
                        ) => ({
                          ...previous,

                          iva:
                            t(
                              "errors.iva_invalid",
                              {
                                defaultValue:
                                  "O IVA deve ser um número válido.",
                              }
                            ),
                        })
                      );

                      return;
                    }

                    if (
                      parsedIva <
                        0 ||
                      parsedIva >
                        100
                    ) {
                      setErrors(
                        (
                          previous
                        ) => ({
                          ...previous,

                          iva:
                            t(
                              "errors.iva_range",
                              {
                                defaultValue:
                                  "O IVA deve estar entre 0 e 100.",
                              }
                            ),
                        })
                      );

                      return;
                    }

                    dirtyRef.current =
                      true;

                    setCarrier(
                      (
                        previous
                      ) => {
                        const next =
                          {
                            ...previous,

                            iva:
                              parsedIva.toFixed(
                                2
                              ),
                          };

                        scheduleCacheWrite(
                          next,
                          files
                        );

                        return next;
                      }
                    );

                    clearFieldError(
                      "iva"
                    );
                  }}
                  min="0"
                  max="100"
                  step="0.01"
                  placeholder={t(
                    "placeholders.iva",
                    {
                      defaultValue:
                        "Exemplo: 23",
                    }
                  )}
                  className={`edit-carrier-input ${
                    errors.iva
                      ? "edit-carrier-input-error"
                      : ""
                  }`}
                />

                {errors.iva && (
                  <p className="edit-carrier-field-error">
                    {
                      errors.iva
                    }
                  </p>
                )}
              </div>

              <div className="edit-carrier-field">
                <label>
                  {t(
                    "fields.invoiceDeliveryMethod"
                  )}
                </label>

                <Select
                  options={
                    invoiceDeliveryOptions
                  }
                  isMulti
                  closeMenuOnSelect={
                    false
                  }
                  hideSelectedOptions={
                    false
                  }
                  value={
                    invoiceDeliveryOptions.filter(
                      (
                        option
                      ) =>
                        (
                          carrier.invoiceDeliveryMethod ||
                          []
                        ).includes(
                          option.value
                        )
                    )
                  }
                  onChange={(
                    selected
                  ) => {
                    dirtyRef.current =
                      true;

                    setCarrier(
                      (
                        previous
                      ) => {
                        const next =
                          {
                            ...previous,

                            invoiceDeliveryMethod:
                              selected
                                ? selected.map(
                                    (
                                      option
                                    ) =>
                                      option.value
                                  )
                                : [],
                          };

                        scheduleCacheWrite(
                          next,
                          files
                        );

                        return next;
                      }
                    );

                    clearFieldError(
                      "invoiceDeliveryMethod"
                    );
                  }}
                  placeholder={t(
                    "placeholders.invoiceDeliveryMethod"
                  )}
                  styles={
                    multiInlineScrollStyles
                  }
                  classNamePrefix="edit-carrier-select"
                />

                {errors.invoiceDeliveryMethod && (
                  <p className="edit-carrier-dropdown-field-error">
                    {
                      errors.invoiceDeliveryMethod
                    }
                  </p>
                )}
              </div>

              <div className="edit-carrier-field">
                <label htmlFor="invoiceEmail">
                  {t(
                    "fields.invoiceEmail"
                  )}
                </label>

                <input
                  type="email"
                  id="invoiceEmail"
                  name="invoiceEmail"
                  value={
                    carrier.invoiceEmail ||
                    ""
                  }
                  onChange={
                    handleInputChange
                  }
                  placeholder={t(
                    "placeholders.invoiceEmail"
                  )}
                  className={`edit-carrier-input ${
                    errors.invoiceEmail
                      ? "edit-carrier-input-error"
                      : ""
                  }`}
                />

                {errors.invoiceEmail && (
                  <p className="edit-carrier-field-error">
                    {
                      errors.invoiceEmail
                    }
                  </p>
                )}
              </div>
            </div>

            <div className="edit-carrier-grid edit-carrier-grid--1">
              <div className="edit-carrier-field">
                <label htmlFor="additionalNotes">
                  {t(
                    "fields.additionalNotes"
                  )}
                </label>

                <textarea
                  id="additionalNotes"
                  name="additionalNotes"
                  placeholder={t(
                    "placeholders.additionalNotes"
                  )}
                  value={
                    carrier.additionalNotes ||
                    ""
                  }
                  onChange={(
                    event
                  ) => {
                    const value =
                      event.target.value.slice(
                        0,
                        500
                      );

                    dirtyRef.current =
                      true;

                    setCarrier(
                      (
                        previous
                      ) => {
                        const next =
                          {
                            ...previous,

                            additionalNotes:
                              value,
                          };

                        scheduleCacheWrite(
                          next,
                          files
                        );

                        return next;
                      }
                    );
                  }}
                  rows="4"
                  className="edit-carrier-textarea-field"
                />

                <div className="edit-carrier-character-counter">
                  {(
                    carrier.additionalNotes ||
                    ""
                  ).length}{" "}
                  / 500
                </div>
              </div>
            </div>
          </section>
        </form>
      </div>
    </div>
  );
}